import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { CityTypeahead } from '../../components/CityTypeahead'
import { FormField } from '../../components/FormField'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { RecordLink } from '../../components/RecordLink'
import { apiErrorMessage } from '../../lib/errors'
import { JUST_SAVED } from '../fmcsa/arrival'
import { formatPercentage } from '../../lib/format'
import { parseAmount } from '../../lib/number'
import { formatPhoneTyping } from '../../lib/phone'
import { formatMcNumber } from '../brokers/format'
import { getCarrier, registerCarrier, updateCarrier, type CarrierPayload } from './api'
import {
  messageFor,
  toCarrierFormErrors,
  withoutCarrierErrorsFor,
  type FormErrors,
} from './errors'
import { CHANGED_ELSEWHERE } from '../../lib/formErrors'
import { ChangedElsewhereNotice } from '../../components/ChangedElsewhereNotice'

const emptyForm = {
  name: '',
  mcNumber: '',
  usdotNumber: '',
  taxId: '',
  commissionRate: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  phone: '',
  email: '',
}

type CarrierForm = typeof emptyForm

const noErrors: FormErrors = { fields: {}, summary: [] }

/**
 * One form for registering and editing a carrier — the broker form's shape with the
 * platform's money fact in it: the commission rate, one per carrier (TRKC-61).
 * Saving lands on the carrier's detail page, where the fleet gets added.
 */
export function CarrierFormPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditing = id !== undefined

  const [form, setForm] = useState<CarrierForm>(emptyForm)
  const [errors, setErrors] = useState<FormErrors>(noErrors)
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  const [isLoading, setIsLoading] = useState(isEditing)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // The reading this form was opened on. Set when the record loads and by "load what is
  // saved" — never by a panel's re-read, which would adopt somebody else's version and
  // let the next save overwrite what they wrote.
  const [version, setVersion] = useState<string | null>(null)
  // The carrier's name as saved, for the trail (TRKC-97) — never as it is being typed.
  const [savedName, setSavedName] = useState<string | null>(null)
  const [isLoadingSaved, setIsLoadingSaved] = useState(false)

  useEffect(() => {
    if (id === undefined) {
      return
    }

    let active = true

    setIsLoading(true)
    setLoadFailure(null)
    setErrors(noErrors)

    void (async () => {
      try {
        const carrier = await getCarrier(id)
        if (!active) {
          return
        }

        setVersion(carrier.updatedAt)
        setSavedName(carrier.name)
        setForm({
          name: carrier.name,
          mcNumber: formatMcNumber(carrier.mcNumber) ?? '',
          usdotNumber: carrier.usdotNumber ?? '',
          taxId: carrier.taxId ?? '',
          commissionRate: String(carrier.commissionPercentage),
          line1: carrier.address.line1,
          line2: carrier.address.line2 ?? '',
          city: carrier.address.city,
          state: carrier.address.state,
          postalCode: carrier.address.postalCode,
          phone: carrier.phone ?? '',
          email: carrier.email ?? '',
        })
      } catch (caught) {
        if (active) {
          setLoadFailure({ error: caught })
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
    // Only the id — `t` here would refetch on a language switch and discard edits.
  }, [id])

  function set(field: keyof CarrierForm, value: string) {
    setForm((current) => ({ ...current, [field]: value }))
    setErrors((current) => withoutCarrierErrorsFor(current, field))
  }

  /**
   * The deliberate step after a refusal: this carrier as it is stored now, into this
   * form, version included.
   */
  async function loadWhatIsSaved() {
    if (id === undefined) {
      return
    }

    setIsLoadingSaved(true)

    try {
      const carrier = await getCarrier(id)

      setVersion(carrier.updatedAt)
      setSavedName(carrier.name)
      setForm({
        name: carrier.name,
        mcNumber: formatMcNumber(carrier.mcNumber) ?? '',
        usdotNumber: carrier.usdotNumber ?? '',
        taxId: carrier.taxId ?? '',
        commissionRate: String(carrier.commissionPercentage),
        line1: carrier.address.line1,
        line2: carrier.address.line2 ?? '',
        city: carrier.address.city,
        state: carrier.address.state,
        postalCode: carrier.address.postalCode,
        phone: carrier.phone ?? '',
        email: carrier.email ?? '',
      })
      setErrors(noErrors)
    } catch (caught) {
      setErrors(toCarrierFormErrors(caught))
    } finally {
      setIsLoadingSaved(false)
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors(noErrors)

    // The rate is required and numeric before it travels; the 0-100 range is the
    // domain's call, and it answers with its own code.
    const commissionRate = parseAmount(form.commissionRate)
    if (commissionRate === null) {
      setErrors({ fields: { commissionRate: 'invalid_commission' }, summary: [] })
      return
    }

    const payload: CarrierPayload = {
      name: form.name.trim(),
      mcNumber: blankToNull(form.mcNumber),
      usdotNumber: blankToNull(form.usdotNumber),
      taxId: blankToNull(form.taxId),
      commissionPercentage: commissionRate,
      address: {
        line1: form.line1.trim(),
        line2: blankToNull(form.line2),
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
      },
      phone: blankToNull(form.phone),
      email: blankToNull(form.email),
      version: version ?? undefined,
    }

    setIsSubmitting(true)

    try {
      const saved = id === undefined
        ? await registerCarrier(payload)
        : await updateCarrier(id, payload)

      // On to the detail, where the fleet lives — a fresh carrier's next step is
      // adding its drivers and equipment, not finding it in the book again. Stamped
      // as a save, so the detail asks the register by itself this once (TRKC-79).
      void navigate(`/carriers/${saved.id}`, { replace: true, state: JUST_SAVED })
    } catch (caught) {
      setErrors(toCarrierFormErrors(caught))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <p className="session-check">{t('carriers.loading')}</p>
  }

  if (loadFailure !== null) {
    return (
      <section className="page">
        <Breadcrumbs trail={[{ label: t('nav.carriers'), to: '/carriers' }, { label: t('carriers.editTitle') }]} />
        <header className="page__header">
          <h1>{t('carriers.editTitle')}</h1>
        </header>
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'carriers.loadError')}
        </p>
        <p>
          <Link to="/carriers">{t('carriers.backToList')}</Link>
        </p>
      </section>
    )
  }

  const fieldText = (name: keyof CarrierForm) => {
    const code = errors.fields[name]
    return code === undefined
      ? undefined
      : messageFor(code, t, 'carriers.saveError', errors.subjects?.[code])
  }

  // A number another record already holds: the sentence names that record, and the
  // way to it rides along — "correct the original first" is one click (TRKC-83).
  const field = (name: keyof CarrierForm) => {
    const code = errors.fields[name]
    const subject = code === undefined ? undefined : errors.subjects?.[code]
    const text = fieldText(name)

    return subject === undefined ? text : (
      <>
        {text} <RecordLink subject={subject} />
      </>
    )
  }

  return (
    <section className="page">
      <Breadcrumbs
        trail={
          isEditing
            ? [
                { label: t('nav.carriers'), to: '/carriers' },
                { label: savedName, to: `/carriers/${id}` },
                { label: t('breadcrumbs.edit') },
              ]
            : [{ label: t('nav.carriers'), to: '/carriers' }, { label: t('breadcrumbs.newCarrier') }]
        }
      />
      <header className="page__header">
        <h1>{isEditing ? t('carriers.editTitle') : t('carriers.registerTitle')}</h1>
        <p className="page__subtitle">{t('carriers.formSubtitle')}</p>
      </header>

      {errors.summary.includes(CHANGED_ELSEWHERE) ? (
        <ChangedElsewhereNotice onLoadSaved={() => void loadWhatIsSaved()} isLoading={isLoadingSaved} />
      ) : (
        errors.summary.length > 0 && (
          <div className="alert" role="alert">
            {errors.summary.map((code) => (
              <p key={code}>{messageFor(code, t, 'carriers.saveError', errors.subjects?.[code])}</p>
            ))}
          </div>
        )
      )}

      <form className="form form--stacked" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="fieldset">
          <legend>{t('carriers.identity')}</legend>

          <FormField
            name="name"
            label={t('carriers.name')}
            value={form.name}
            onChange={(value) => set('name', value)}
            error={field('name')}
            maxLength={200}
            required
            width="wide"
          />
          <FormField
            name="mcNumber"
            label={t('carriers.mcNumber')}
            value={form.mcNumber}
            onChange={(value) => set('mcNumber', value)}
            error={field('mcNumber')}
            placeholder="MC-123456"
          />
          <FormField
            name="usdotNumber"
            label={t('carriers.usdotNumber')}
            value={form.usdotNumber}
            onChange={(value) => set('usdotNumber', value)}
            error={field('usdotNumber')}
          />
          <FormField
            name="taxId"
            label={t('carriers.taxId')}
            value={form.taxId}
            onChange={(value) => set('taxId', value)}
            error={field('taxId')}
            maxLength={32}
          />
          <FormField
            name="commissionRate"
            label={t('carriers.commissionRate')}
            value={form.commissionRate}
            onChange={(value) => set('commissionRate', value)}
            error={field('commissionRate')}
            inputMode="decimal"
            // Reads as the percentage it is at rest; raw digits while editing.
            display={(value) => {
              const rate = parseAmount(value)
              return rate === null ? value : formatPercentage(rate, i18n.language)
            }}
            required
            width="narrow"
          />
        </fieldset>

        <fieldset className="fieldset">
          <legend>{t('carriers.address')}</legend>

          <FormField
            name="line1"
            label={t('carriers.line1')}
            value={form.line1}
            onChange={(value) => set('line1', value)}
            error={field('line1')}
            maxLength={200}
            required
            width="wide"
          />
          <FormField
            name="line2"
            label={t('carriers.line2')}
            value={form.line2}
            onChange={(value) => set('line2', value)}
            error={field('line2')}
            maxLength={200}
            width="wide"
          />
          <CityTypeahead
            name="city"
            label={t('carriers.city')}
            value={form.city}
            onChange={(value) => set('city', value)}
            onSelect={({ city, state }) => {
              set('city', city)
              set('state', state)
            }}
            error={fieldText('city')}
            maxLength={100}
            required
            preferredState={form.state}
          />
          <FormField
            name="state"
            label={t('carriers.state')}
            value={form.state}
            onChange={(value) => set('state', value)}
            error={field('state')}
            maxLength={2}
            placeholder="FL"
            required
            width="narrow"
          />
          <FormField
            name="postalCode"
            label={t('carriers.postalCode')}
            value={form.postalCode}
            onChange={(value) => set('postalCode', value)}
            error={field('postalCode')}
            maxLength={10}
            placeholder="33131"
            required
          />
        </fieldset>

        <fieldset className="fieldset">
          <legend>{t('carriers.commercial')}</legend>

          <FormField
            name="phone"
            label={t('carriers.phone')}
            value={form.phone}
            onChange={(value) => set('phone', formatPhoneTyping(form.phone, value))}
            error={field('phone')}
            type="tel"
            placeholder="(305) 555-0142"
          />
          <FormField
            name="email"
            label={t('carriers.email')}
            value={form.email}
            onChange={(value) => set('email', value)}
            error={field('email')}
            type="email"
            maxLength={256}
          />
        </fieldset>

        <div className="form__actions">
          <button type="submit" className="button" disabled={isSubmitting}>
            {isSubmitting ? t('carriers.saving') : t('carriers.save')}
          </button>
        </div>
      </form>
    </section>
  )
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}
